import { IPgnVariantMeta, IPgnMove, PgnVariant } from '@libs/chess/pgn';

type MovePosition = {
  variant: IPgnMove[];
  index: number;
  cur: IPgnMove;
  next?: IPgnMove;
  prev?: IPgnMove | null;
  path: { parent: IPgnMove; branch: PgnVariant }[];
  playSequence: IPgnMove[];
};

type Fork = {
  nextMoves: IPgnMove[];
  variant: IPgnMove[];
  index: number;
  prevParent?: IPgnMove;
  position: MovePosition;
};

/*
    ____,-------------------------------,____
    \   |      Редактирвоание ходов     |   /
    /___|-------------------------------|___\
*/

// Изменить аттрибуты хода
export function updateMove(
  targetMove: IPgnMove,
  data: Partial<Omit<IPgnMove, 'alts'>>,
  moves: IPgnMove[],
): { nextMoves: IPgnMove[]; nextSelected?: IPgnMove } {
  const fork = createFork(targetMove, moves);
  if (!fork) {
    console.error('PgnEditor: updateMove: move not found', {
      move: targetMove,
      moves,
    });
    return { nextMoves: moves, nextSelected: targetMove };
  }
  const nextSelected = { ...targetMove, ...data };
  fork.variant.splice(fork.index, 1, nextSelected);
  return { nextMoves: fork.nextMoves, nextSelected };
}

// Добавить новые ходы:
// - если задан `targetMove` — после него (или создать вариант);
// - иначе — продолжаем с первого хода основной ветки, либо создаём новый
//   вариант к нему, либо целиком новую ветку (если ходов ещё не было).
export function addMoves(
  targetMove: IPgnMove | undefined,
  newMoves: IPgnMove[],
  moves: IPgnMove[],
  createId: () => string,
  options: { readonlyMainVariant?: boolean } = {},
): { nextMoves: IPgnMove[]; nextSelected?: IPgnMove } {
  if (!newMoves.length) return { nextMoves: moves };

  if (targetMove) {
    // продолжаем ветку
    return addMovesAfter(targetMove, newMoves, moves, createId, options);
  }

  const firstMove = moves[0];
  if (!firstMove) {
    // невозможно создать подвариант, если нет ходов
    if (options.readonlyMainVariant) return { nextMoves: moves };

    // создаём основную ветку
    return {
      nextMoves: newMoves,
      nextSelected: newMoves[newMoves.length - 1],
    };
  }

  const similar = getSimilarAlt(newMoves[0]!.san, firstMove);
  if (!similar) {
    // последовательность уникальная — добавляем как вариант к первому ходу
    return addVariant(firstMove, newMoves, moves, createId);
  }

  // нашли совпадение — продолжаем от него
  const tail = newMoves.slice(1);
  if (!tail.length) return { nextMoves: moves, nextSelected: similar };
  return addMovesAfter(similar, tail, moves, createId, options);
}

// Добавить новые ходы после `targetMove` (или создавть вариант)
function addMovesAfter(
  targetMove: IPgnMove,
  newMoves: IPgnMove[],
  moves: IPgnMove[],
  createId: () => string,
  options: { readonlyMainVariant?: boolean } = {},
): { nextMoves: IPgnMove[]; nextSelected?: IPgnMove } {
  const fork = createFork(targetMove, moves);
  if (!fork) {
    console.error('PgnEditor: addMovesAfter: move not found', {
      move: targetMove,
      moves,
    });
    return { nextMoves: moves, nextSelected: targetMove };
  }
  const { nextMoves, variant, index } = fork;

  // пропускаем дубликаты: совпадения в текущей ветке и её альтернативах
  const { seq, lastSimilarMove } = filterOutSimilarAlts(newMoves, variant.slice(index + 1));
  if (lastSimilarMove && seq.length === 0) {
    // ревертим: всё уже есть
    return { nextMoves: moves, nextSelected: lastSimilarMove };
  }
  if (lastSimilarMove) {
    return addMovesAfter(lastSimilarMove, seq, moves, createId, options);
  }

  const isMainVariant = !targetMove.altId;
  let nextMoveIndex = index + 1;
  let nextMove = variant[nextMoveIndex];

  // ВАЖНО: если добавляется новый ход к последнему ходу основной ветки, и
  // запрещено менять основную ветку (как например при тарнсляциях), то
  // создаём новую ветку В САМОМ последнем ходе и дублируем этот ход в
  // самого себя, а уже после добавляем новый.
  // Такой компромис: вместо "1. g4 d5 2. Nc3" получим "1. g4 d5 (1... d5 2. Nc3)"
  if (!nextMove && options.readonlyMainVariant && isMainVariant) {
    nextMoveIndex = index;
    nextMove = targetMove;
    seq.unshift({
      ...nextMove,
      alts: undefined,
      id: nextMove.id + '-dupe', // помечаем как дубликат
    });
  }

  // id создаём только если реально потребуется новая подветка
  const newAltId = nextMove ? createId() : undefined;

  // куда попадут новые ходы: в текущую ветку (inline) или в новую подветку
  const targetAltId = newAltId ?? targetMove.altId;
  // проставляем altId на каждый новый ход
  for (let k = 0; k < seq.length; k++) {
    seq[k] = { ...seq[k]!, altId: targetAltId };
  }
  const nextSelected = seq[seq.length - 1]!;

  if (!nextMove) {
    variant.push(...seq);
  } else {
    // sic! ветка прицепляется к nextMove (его .alts), а не к targetMove
    const alt = new PgnVariant({
      id: newAltId!,
      parentMoveId: nextMove.id,
      moves: seq,
    });
    variant.splice(nextMoveIndex, 1, {
      ...nextMove,
      alts: nextMove.alts ? [...nextMove.alts, alt] : [alt],
    });
  }
  return { nextMoves, nextSelected };
}

// Удалить ход (вместе с хвостом ветки после него)
//
// ВАЖНО: при удалении хода теряются и его подветки (alts) — они хранятся
// на самом ходе. Чтобы сохранить альтернативы, используйте
// promoteVariant(..., 'replace', ...) — он поднимет одну из подветок на
// место хода, а остальные сохранит как её соседей.
export function deleteMove(
  targetMove: IPgnMove,
  moves: IPgnMove[],
): { nextMoves: IPgnMove[]; nextSelected?: IPgnMove } {
  const fork = createFork(targetMove, moves);
  if (!fork) {
    console.error('PgnEditor: deleteMove: move not found', {
      move: targetMove,
      moves,
    });
    return { nextMoves: moves };
  }
  const { nextMoves, variant, index, prevParent, position } = fork;

  if (index === 0 && prevParent) {
    prevParent.alts = prevParent.alts?.filter((l) => l.moves !== variant);
    if (prevParent.alts?.length === 0) prevParent.alts = undefined;
  } else {
    variant.splice(index);
  }
  return { nextMoves, nextSelected: position.prev ?? undefined };
}

// Создать вариант для `targetMove` — новые ходы попадают в `targetMove.alts`
export function addVariant(
  targetMove: IPgnMove,
  newMoves: IPgnMove[],
  moves: IPgnMove[],
  createId: () => string,
): { nextMoves: IPgnMove[]; nextSelected?: IPgnMove } {
  if (!newMoves.length) return { nextMoves: moves };

  const same = getSimilarAlt(newMoves[0]!.san, targetMove);
  if (same) {
    return { nextMoves: moves, nextSelected: same };
  }

  const fork = createFork(targetMove, moves);
  if (!fork) {
    console.error('PgnEditor: addVariant: move not found', {
      move: targetMove,
      moves,
    });
    return { nextMoves: moves };
  }

  const altId = createId();
  const seq = newMoves.map((m) => ({ ...m, altId }));
  const alt = new PgnVariant({
    id: altId,
    parentMoveId: targetMove.id,
    moves: seq,
  });
  fork.variant.splice(fork.index, 1, {
    ...targetMove,
    alts: targetMove.alts ? [...targetMove.alts, alt] : [alt],
  });
  return { nextMoves: fork.nextMoves, nextSelected: seq[seq.length - 1] };
}

// Сделать вариант основным (поднять/promote):
export function promoteVariant(
  targetVariant: PgnVariant,
  mode: 'swap' | 'replace',
  moves: IPgnMove[],
  createId: () => string,
): { nextMoves: IPgnMove[]; nextSelected?: IPgnMove } {
  const parent = findMoveById(targetVariant.parentMoveId, moves);
  if (!parent) {
    console.error('PgnEditor: promoteVariant: parent move not found', {
      variant: targetVariant,
    });
    return { nextMoves: moves };
  }

  const fork = createFork(parent, moves);
  if (!fork) return { nextMoves: moves };
  const { nextMoves, variant, index, position } = fork;

  const promotedHead = targetVariant.moves[0]!;
  const promotedAltId = parent.altId;

  // sic! prelude промоутнутой подветки придётся пожертвовать

  // соседи переезжают на промоутнутый ход — обновляем parentMoveId
  const liftedSiblings = parent
    .alts!.filter((l) => l !== targetVariant)
    .map((l) => new PgnVariant({ ...l, parentMoveId: promotedHead.id }));

  let alts: PgnVariant[] = liftedSiblings;
  if (mode === 'swap') {
    // бывший хвост уходит подветкой
    const formerTailAltId = createId();
    const formerTailHead: IPgnMove = {
      ...parent,
      altId: formerTailAltId,
      alts: undefined,
    };
    const formerTailRest = variant.slice(index + 1).map((m) => ({ ...m, altId: formerTailAltId }));
    const formerTailAlt = new PgnVariant({
      id: formerTailAltId,
      parentMoveId: promotedHead.id,
      moves: [formerTailHead, ...formerTailRest],
    });
    alts = [...liftedSiblings, formerTailAlt];
  }

  const promotedFirst: IPgnMove = {
    ...promotedHead,
    altId: promotedAltId,
    alts: alts.length ? alts : undefined,
  };
  const promotedTail = [
    promotedFirst,
    ...targetVariant.moves.slice(1).map((m) => ({ ...m, altId: promotedAltId })),
  ];
  variant.splice(index, variant.length - index, ...promotedTail);

  const nextSelected = mode === 'swap' ? promotedFirst : (position.prev ?? undefined);
  return { nextMoves, nextSelected };
}

// Поставить вариант выше/ниже (ближе/дальше)
export function moveVariant(
  targetVariant: PgnVariant,
  direction: 'up' | 'down',
  moves: IPgnMove[],
): { nextMoves: IPgnMove[] } {
  const parent = findMoveById(targetVariant.parentMoveId, moves);
  if (!parent?.alts) {
    console.error('PgnEditor: moveVariant: parent move not found', {
      variant: targetVariant,
    });
    return { nextMoves: moves };
  }
  const idx = parent.alts.indexOf(targetVariant);
  const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
  if (idx === -1 || swapIdx < 0 || swapIdx >= parent.alts.length) {
    return { nextMoves: moves };
  }

  const fork = createFork(parent, moves);
  if (!fork) return { nextMoves: moves };
  const { nextMoves, variant, index } = fork;

  const nextAlts = [...parent.alts];
  nextAlts[idx] = parent.alts[swapIdx]!;
  nextAlts[swapIdx] = parent.alts[idx]!;
  variant.splice(index, 1, { ...parent, alts: nextAlts });

  return { nextMoves };
}

// Обновить метаданные всего варианта (комментарий/стрелки)
export function updateVariant(
  targetVariant: PgnVariant,
  moves: IPgnMove[],
  action: { action: 'update'; data: IPgnVariantMeta },
): { nextMoves: IPgnMove[]; nextLine?: PgnVariant } {
  const parent = findMoveById(targetVariant.parentMoveId, moves);
  if (!parent) {
    console.error('PgnEditor: updateVariant: parent move not found', {
      variant: targetVariant,
      moves,
    });
    return { nextMoves: moves };
  }

  const fork = createFork(parent, moves);
  if (!fork) return { nextMoves: moves };

  const nextLine = new PgnVariant({
    ...targetVariant,
    ...action.data,
    id: targetVariant.id,
    moves: targetVariant.moves,
  });
  fork.variant.splice(fork.index, 1, {
    ...parent,
    alts: parent.alts?.map((l) => (l.id === targetVariant.id ? nextLine : l)),
  });
  return { nextMoves: fork.nextMoves, nextLine };
}

/*
    ____,-------------------------------,____
    \   |          Поиск ходов          |   /
    /___|-------------------------------|___\
*/

export function findMovePosition(
  moveId: string,
  moves: IPgnMove[],
  _prevMove: IPgnMove | null = null,
): MovePosition | undefined {
  for (let i = 0; i < moves.length; i++) {
    const m = moves[i]!;

    if (m.id === moveId) {
      return {
        variant: moves,
        index: i,
        cur: m,
        next: moves[i + 1],
        prev: moves[i - 1] ?? _prevMove,
        path: [],
        playSequence: moves.slice(0, i + 1),
      };
    }

    if (m.alts) {
      for (const a of m.alts) {
        const r = findMovePosition(moveId, a.moves, moves[i - 1]);
        if (r) {
          return {
            ...r,
            playSequence: [...moves.slice(0, i), ...r.playSequence],
            path: r.path.length
              ? [{ parent: m, branch: a }, ...r.path]
              : [{ parent: m, branch: a }],
          };
        }
      }
    }
  }
}

export function findMoveById(id: string, moves: IPgnMove[]): IPgnMove | undefined {
  for (let i = 0; i < moves.length; i++) {
    const m = moves[i]!;
    if (id === m.id) return m;

    if (m.alts) {
      for (let j = 0; j < m.alts.length; j++) {
        const q = findMoveById(id, m.alts[j]!.moves);
        if (q) return q;
      }
    }
  }
  return undefined;
}

export function findVariantById(id: string, moves: IPgnMove[]): PgnVariant | undefined {
  for (const m of moves) {
    if (!m.alts) continue;
    for (const a of m.alts) {
      if (a.id === id) return a;
      const nested = findVariantById(id, a.moves);
      if (nested) return nested;
    }
  }
  return undefined;
}

export function getSimilarAlt(san: string, move?: IPgnMove) {
  if (!move) return undefined;
  if (move.san === san) return move;
  return move.alts?.find((l) => l.moves[0]?.san === san)?.moves[0];
}

/*
    ____,-------------------------------,____
    \   |           Утилиты             |   /
    /___|-------------------------------|___\
*/

// Копия дерева для внесения изменений в ход.
// Возвращаем новый variant, в котором можно править ходы, не задевая соседние ветки
function createFork(targetMove: IPgnMove, moves: IPgnMove[]): Fork | undefined {
  const position = findMovePosition(targetMove.id, moves);
  if (!position) return undefined;

  const nextMoves = [...moves];
  let variant = nextMoves;
  let prevParent: IPgnMove | undefined;
  for (const { parent, branch } of position.path) {
    const nextVariant = [...branch.moves];
    const nextParent: IPgnMove = {
      ...parent,
      alts: parent.alts?.map((l) =>
        l === branch ? new PgnVariant({ ...l, moves: nextVariant }) : l,
      ),
    };
    variant.splice(variant.indexOf(parent), 1, nextParent);
    prevParent = nextParent;
    variant = nextVariant;
  }

  return {
    nextMoves,
    variant,
    index: variant.indexOf(targetMove),
    prevParent,
    position,
  };
}

function filterOutSimilarAlts(
  seq: IPgnMove[],
  moves: IPgnMove[],
): { seq: IPgnMove[]; moves: IPgnMove[]; lastSimilarMove?: IPgnMove } {
  // проверим основной вариант
  let foundIndex: number | null = null;
  let i = 0;
  while (seq[i] && seq[i]!.san === moves[i]?.san) foundIndex = i++;

  const lastSimilarMove = foundIndex === null ? undefined : moves[foundIndex];
  const seqTail = foundIndex === null ? seq : seq.slice(foundIndex + 1);
  const movesTail = foundIndex === null ? moves : moves.slice(foundIndex + 1);

  // поищем в альтернативах
  const alts = movesTail[0]?.alts;
  if (seqTail.length && alts) {
    for (const alt of alts) {
      const r = filterOutSimilarAlts(seqTail, alt.moves);
      if (r.lastSimilarMove) return r;
    }
  }

  return { seq: seqTail, moves: movesTail, lastSimilarMove };
}
